import React, { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import posthog from 'posthog-js'

/**
 * PageTracker — fire-and-forget page-view beacon on every SPA route change.
 *
 * Renders nothing; must sit INSIDE <Router> (see src/router.tsx). Two sinks:
 *  1. First-party: POST /analytics/pageview → powers the Queue Admin Stats
 *     tab (Site Activity: top pages, views/day, page→page flows). Sent via
 *     fetch keepalive with NO auth header — the endpoint is public and
 *     rate-limited, and skipping the Privy token keeps this off the auth path.
 *  2. PostHog `$pageview` — posthog.init runs in main.tsx but SPA route
 *     changes were never captured; this closes that gap for free.
 *
 * Privacy: only the path (backend further normalizes ids), the previous path,
 * and a random localStorage session id are sent. No user id, no query strings.
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://cyberlete.herokuapp.com/api/v1'
const SID_KEY = 'cy_pv_sid'

function sessionId(): string | null {
  try {
    let sid = localStorage.getItem(SID_KEY)
    if (!sid || !/^[A-Za-z0-9_-]{8,64}$/.test(sid)) {
      sid = Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) =>
        'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'.charAt(b % 62)
      ).join('')
      localStorage.setItem(SID_KEY, sid)
    }
    return sid
  } catch {
    return null // storage blocked (private mode) → views still count, uniques won't
  }
}

const PageTracker: React.FC = () => {
  const { pathname } = useLocation()
  const prevPath = useRef<string | null>(null)

  useEffect(() => {
    if (pathname === prevPath.current) return
    const ref = prevPath.current
    prevPath.current = pathname

    try {
      void fetch(`${API_BASE}/analytics/pageview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: pathname, ref, sid: sessionId() }),
        keepalive: true,
      }).catch(() => {})
    } catch {
      /* analytics must never break navigation */
    }

    try {
      posthog.capture('$pageview')
    } catch {
      /* posthog optional */
    }
  }, [pathname])

  return null
}

export default PageTracker
